import { PlainButton } from "@jugl-web/ui-components/cross-platform/PlainButton";
import { Button } from "@jugl-web/ui-components/cross-platform/Button";
import { useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { ReactComponent as GoBackIcon } from "./assets/go-back-icon.svg";
import { useBoardTemplatesModalProvider } from "../../providers/useBoardTemplatesModalProvider";
import { ReactComponent as UseTemplateIcon } from "./assets/use-template.svg";
import { ReactComponent as ExternalLinkIcon } from "./assets/external-link.svg";
import { ReactComponent as Section0Icon } from "./assets/section-0.svg";
import { ReactComponent as Section1Icon } from "./assets/section-1.svg";
import { ReactComponent as Section2Icon } from "./assets/section-2.svg";
import { ReactComponent as TaskPillIcon } from "./assets/task-pill.svg";
import { CTABanner } from "./components/CTABanner/CTABanner";

const getDemoUrl = (templateId: string) =>
  `https://demo.jugl.com/ebccc659-97bc-44fc-bf5a-cb2819335c57/tasks/board/${templateId}`;

export const BoardTemplateDetails = ({
  templateId,
}: {
  templateId: string;
}) => {
  const { t } = useTranslations();
  const { tasksApi } = useRestApiProvider();

  const { data: template, isLoading } =
    tasksApi.useGetBoardTemplateDetailsQuery(
      templateId
        ? {
            id: templateId,
          }
        : skipToken
    );
  const { setSelectedTemplate, handleUseTemplate } =
    useBoardTemplatesModalProvider();

  if (!template || isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-8">
        <div className="text-lg text-gray-500">
          {t({
            id: "board-templates-drawer.loading",
            defaultMessage: "Loading template details...",
          })}
        </div>
      </div>
    );
  }

  const customFields = template.components.find(
    (component) => component.type === "custom_field"
  );
  const sampleTasks = template.components.find(
    (component) => component.type === "task"
  );

  const isEmpty =
    customFields?.data.length === 0 && sampleTasks?.data.length === 0;

  return (
    <div className="font-secondary flex w-full gap-8 px-10 py-8 ">
      <PlainButton
        className="border-grey-300 flex h-[48px] w-[48px] min-w-[48px] items-center justify-center rounded-xl border-[1px] border-solid outline-double outline-1 outline-transparent transition-all duration-300 hover:border-[#7FBDf7] hover:outline-[#7FBDf7]"
        onClick={() => setSelectedTemplate(null)}
      >
        <GoBackIcon />
      </PlainButton>
      <div className="flex w-full max-w-[905px] flex-col pb-[64px]">
        <div className="pb-[64px]">
          <CTABanner name={template.name} />
        </div>
        <div className="flex w-full items-center gap-3">
          <Section0Icon />

          <div
            className="text-dark text-[20px] font-medium"
            title={template?.name || ""}
          >
            {t({
              id: "common.overview",
              defaultMessage: "Overview",
            })}
          </div>
        </div>
        <div
          className="j__template-details max-w-[70%] pt-4 text-[#828282]"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: template?.full_desc }}
        />
        {/* <div className="flex flex-col gap-[19px] pt-8">
          {template?.useCases?.map((useCase) => (
            <div key={useCase} className="flex items-center gap-4 text-dark">
              <UseCaseCheckIcon />
              <div className="w-[60%]" title={useCase}>
                {useCase}
              </div>
            </div>
          ))}
        </div> */}

        {!isEmpty && (
          <div className="pt-[72px]">
            <div className="pl-4">
              <div>
                <div className="flex items-center gap-3">
                  <Section1Icon />
                  <div className="text-dark font-secondary flex items-center text-xl font-medium leading-[0]">
                    {t(
                      {
                        id: "board-templates-drawer.template-includes-section-1",
                        defaultMessage:
                          "{count} Custom {count, plural, one {field} other {fields}}",
                      },
                      {
                        count: customFields?.data.length ?? 0,
                      }
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 pt-4">
                  {customFields?.data.map((field) => (
                    <div
                      key={field}
                      className="flex items-center rounded-md bg-[#E3F2FD] px-2 py-1 text-sm text-[#1764C0]"
                    >
                      {field}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="pl-4 pt-[48px]">
              <div>
                <div className="flex items-center gap-3">
                  <Section2Icon />
                  <div className="text-dark flex items-center text-xl font-medium leading-[0]">
                    {t(
                      {
                        id: "board-templates-drawer.template-includes-section-2",
                        defaultMessage:
                          "{count} Sample {count, plural, one {Task} other {Tasks}}",
                      },
                      {
                        count: sampleTasks?.data.length ?? 0,
                      }
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 pt-4 pb-[88px]">
                  {sampleTasks?.data.map((task) => (
                    <div
                      key={task}
                      className="bg-grey-100 text-dark-800 flex h-[25px] items-center gap-1 rounded-md px-2 text-sm"
                    >
                      <TaskPillIcon /> {task}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* <div className="pl-4 pt-[48px]">
              <div>
              <div className="flex items-center gap-3">
                <Section2Icon />
                <div className="text-dark flex items-center text-xl font-medium leading-[0]">
                  {t(
                    {
                      id: "board-templates-drawer.template-includes-section-3",
                      defaultMessage:
                        "{count} Automation {count, plural, one {Rule} other {Rules}}",
                    },
                    {
                      count: template?.automationRules?.length ?? 0,
                    }
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 pt-4">
                {template?.automationRules?.map((rule) => (
                  <div
                    key={rule}
                    className="text-sm text-dark-700"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: rule }}
                  />
                ))}
              </div>
            </div> 
            </div> */}
          </div>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-4 border-0 border-t-[2px] border-solid border-[#EEF2F5] bg-white py-[24px]">
        <div className="flex gap-4">
          <Button
            variant="outlined"
            size="small"
            className="font-secondary h-[40px] self-center px-8 font-medium"
            iconEnd={<UseTemplateIcon />}
            onClick={() => handleUseTemplate(templateId)}
          >
            {t({
              id: "board-templates-drawer.use-template-button",
              defaultMessage: "Use Template",
            })}
          </Button>
        </div>
        {template.meta?.board_id && (
          <Button
            size="small"
            className="font-secondary h-[40px] self-center px-8 font-medium"
            iconEnd={<ExternalLinkIcon />}
            onClick={() => {
              if (template.meta?.board_id) {
                window.open(getDemoUrl(template.meta.board_id), "_blank");
              }
            }}
          >
            {t({
              id: "board-templates-drawer.see-live-demo",
              defaultMessage: "See Live Demo",
            })}
          </Button>
        )}
      </div>
    </div>
  );
};
